import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  RefreshCw, Search, DollarSign, XCircle, RotateCcw, 
  AlertTriangle, Calendar, TrendingDown, FileText
} from 'lucide-react';
import { API_BASE_URL } from '@/config/api';
import {
  errorText, fetchRefundSummary, outcomeText, refundOrderPayment, toCents, voidOrderPayment,
} from '@/lib/orderRefunds';

const API_URL = API_BASE_URL;

const REFUND_REASONS = [
  { value: "customer_request", label: "Customer Request" },
  { value: "order_error", label: "Order Error" },
  { value: "quality_issue", label: "Quality Issue" },
  { value: "never_received", label: "Never Received" },
  { value: "duplicate", label: "Duplicate Order" },
  { value: "other", label: "Other" }
];

const REFUND_TYPE_CONFIG = {
  void: { label: 'Void', color: 'bg-red-100 text-red-700', icon: XCircle },
  full_refund: { label: 'Full Refund', color: 'bg-orange-100 text-orange-700', icon: RotateCcw },
  partial_refund: { label: 'Partial', color: 'bg-yellow-100 text-yellow-700', icon: DollarSign }
};

export default function RefundsPage() {
  const context = useOutletContext() || {};
  const { token, restaurant } = context;
  
  const [loading, setLoading] = useState(true);
  const [refundHistory, setRefundHistory] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('30');
  
  // Dialog state
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refundForm, setRefundForm] = useState({
    amount: '',
    reason: '',
    notes: '',
  });
  const [processing, setProcessing] = useState(false);
  // The server's view of the selected order's payment (refundable, already refunded, tender).
  const [summary, setSummary] = useState(null);

  const fetchRefundHistory = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await fetch(`${API_URL}/api/refunds/history?days=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setRefundHistory(data);
    } catch (error) {
      console.error('Error fetching refund history:', error);
      toast.error('Could not load refund history');
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  useEffect(() => {
    if (token) {
      fetchRefundHistory();
    }
  }, [token, fetchRefundHistory]);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  // Voids and refunds go to /api/merchant/orders/{id}/void|refund: a card one
  // is sent to Aster Gateway and counts only once it approved; cash is
  // recorded and handed back by the cashier.
  const finish = (view, orderId, closeDialog) => {
    if (view && view.is_final !== false && view.status === 'approved') {
      toast.success(outcomeText(view, orderId));
      closeDialog();
      setSelectedOrder(null);
      setSummary(null);
      resetForm();
    } else {
      toast.error(outcomeText(view, orderId));
    }
    fetchRefundHistory();
  };

  const handleVoidOrder = async () => {
    if (!selectedOrder || !refundForm.reason) {
      toast.error('Please select a reason');
      return;
    }
    setProcessing(true);
    try {
      const view = await voidOrderPayment(selectedOrder.id, {
        reason: refundForm.reason, notes: refundForm.notes,
      }, authHeaders());
      finish(view, selectedOrder.id, () => setShowVoidDialog(false));
    } catch (error) {
      toast.error(errorText(error, 'The void did not go through'));
    } finally {
      setProcessing(false);
    }
  };

  const handleRefundOrder = async () => {
    if (!selectedOrder || !refundForm.reason || !refundForm.amount || !summary) {
      toast.error('Please fill in all required fields');
      return;
    }
    const amountCents = toCents(refundForm.amount);
    if (amountCents === null) {
      toast.error('Please enter a valid refund amount');
      return;
    }
    setProcessing(true);
    try {
      const view = await refundOrderPayment(selectedOrder.id, {
        amountCents,
        reason: refundForm.reason,
        notes: refundForm.notes,
        refundedCentsBefore: summary.refunded_cents_before,
      }, authHeaders());
      finish(view, selectedOrder.id, () => setShowRefundDialog(false));
    } catch (error) {
      toast.error(errorText(error, 'The refund did not go through'));
    } finally {
      setProcessing(false);
    }
  };

  const loadSummary = async (order) => {
    setSummary(null);
    if (!order) return;
    try {
      const data = await fetchRefundSummary(order.id, authHeaders());
      setSummary(data);
      setRefundForm((prev) => ({ ...prev, amount: ((data.refundable_cents || 0) / 100).toFixed(2) }));
    } catch (error) {
      toast.error(errorText(error, 'Could not load this order\'s payment'));
    }
  };

  const resetForm = () => {
    setRefundForm({
      amount: '',
      reason: '',
      notes: '',
    });
  };

  const openVoidDialog = (order) => {
    setSelectedOrder(order);
    resetForm();
    loadSummary(order);
    setShowVoidDialog(true);
  };

  const openRefundDialog = (order) => {
    setSelectedOrder(order);
    resetForm();
    loadSummary(order);
    setShowRefundDialog(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="refunds-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Refunds & Voids</h1>
          <p className="text-gray-500 text-sm md:text-base">Manage order refunds and voids</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-32">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="icon"
            onClick={fetchRefundHistory}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="refunds-load-error">
          <span>Could not load refund history.</span>
          <Button variant="outline" size="sm" onClick={fetchRefundHistory}>Try again</Button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Total Refunded</p>
                <p className="text-xl md:text-2xl font-bold text-red-600">
                  {formatCurrency(refundHistory?.total_refunded)}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Voids</p>
                <p className="text-xl md:text-2xl font-bold text-orange-600">
                  {refundHistory?.void_count || 0}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Refunds</p>
                <p className="text-xl md:text-2xl font-bold text-yellow-600">
                  {refundHistory?.refund_count || 0}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <RotateCcw className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Total Transactions</p>
                <p className="text-xl md:text-2xl font-bold text-blue-600">
                  {refundHistory?.total_transactions || 0}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reason Breakdown */}
      {refundHistory?.reason_breakdown && Object.keys(refundHistory.reason_breakdown).length > 0 && (
        <Card className="bg-white border-0 shadow-sm">
          <CardHeader className="p-4 md:p-6 pb-2">
            <CardTitle className="text-base md:text-lg">Refund Reasons</CardTitle>
            <CardDescription className="text-xs md:text-sm">Breakdown by reason</CardDescription>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="space-y-3">
              {Object.entries(refundHistory.reason_breakdown).map(([reason, amount]) => {
                const reasonLabel = REFUND_REASONS.find(r => r.value === reason)?.label || reason;
                const percent = (amount / refundHistory.total_refunded) * 100;
                return (
                  <div key={reason}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{reasonLabel}</span>
                      <span className="text-sm text-gray-500">{formatCurrency(amount)}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-500 rounded-full"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Refunds Table */}
      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        <CardHeader className="p-4 md:p-6 pb-2">
          <CardTitle className="text-base md:text-lg">Recent Refunds & Voids</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
            </div>
          ) : refundHistory?.recent_refunds?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Order</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="hidden md:table-cell">Reason</TableHead>
                  <TableHead className="hidden sm:table-cell">Processed By</TableHead>
                  <TableHead className="hidden md:table-cell">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {refundHistory.recent_refunds.map((refund) => {
                  const typeConfig = REFUND_TYPE_CONFIG[refund.refund_type] || REFUND_TYPE_CONFIG.partial_refund;
                  const TypeIcon = typeConfig.icon;
                  return (
                    <TableRow key={refund.id}>
                      <TableCell className="font-medium">#{refund.order_id}</TableCell>
                      <TableCell>
                        <Badge className={`${typeConfig.color} text-xs`}>
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {typeConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-red-600">
                        -{formatCurrency(refund.amount)}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {REFUND_REASONS.find(r => r.value === refund.reason)?.label || refund.reason}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-gray-500">
                        {refund.processed_by || '-'}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-gray-500 text-sm">
                        {refund.created_at ? new Date(refund.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <RotateCcw className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p>No refunds or voids in this period</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Void Dialog */}
      <Dialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" />
              Void Order
            </DialogTitle>
            <DialogDescription>
              This will cancel the order and reverse any pending charges.
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="p-3 bg-gray-50 rounded-lg mb-4">
              <p className="font-semibold">Order #{selectedOrder.id}</p>
              <p className="text-sm text-gray-500">Total: {formatCurrency(selectedOrder.total)}</p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label>Reason *</Label>
              <Select
                value={refundForm.reason}
                onValueChange={(val) => setRefundForm({ ...refundForm, reason: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  {REFUND_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={refundForm.notes}
                onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })}
                placeholder="Additional details..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoidDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleVoidOrder}
              disabled={processing || !refundForm.reason || !summary?.can_void}
              className="bg-red-600 hover:bg-red-700"
            >
              {processing ? 'Processing...' : 'Void Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-orange-600" />
              Process Refund
            </DialogTitle>
            <DialogDescription>
              Issue a full or partial refund for this order.
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="p-3 bg-gray-50 rounded-lg mb-4">
              <p className="font-semibold">Order #{selectedOrder.id}</p>
              <p className="text-sm text-gray-500">
                Order Total: {formatCurrency(selectedOrder.total)}
                {summary && summary.refunded_cents > 0 && (
                  <span className="text-red-500 ml-2">
                    (Already refunded: {formatCurrency(summary.refunded_cents / 100)})
                  </span>
                )}
              </p>
              {summary && !summary.can_refund && (
                <p className="text-sm text-amber-700 mt-1">
                  {summary.unavailable_reason || 'Nothing is left to refund on this order'}
                </p>
              )}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label>Refund Amount *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                  className="pl-9"
                  placeholder="0.00"
                />
              </div>
              {selectedOrder && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 h-auto text-xs"
                  disabled={!summary}
                  onClick={() => setRefundForm({ ...refundForm, amount: ((summary?.refundable_cents || 0) / 100).toFixed(2) })}
                >
                  Refund all that is left
                </Button>
              )}
            </div>
            
            <div>
              <Label>Reason *</Label>
              <Select
                value={refundForm.reason}
                onValueChange={(val) => setRefundForm({ ...refundForm, reason: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  {REFUND_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <p className="text-xs text-gray-500">
              A card payment is refunded to the card through Aster Gateway; a cash payment is handed back by the cashier.
            </p>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={refundForm.notes}
                onChange={(e) => setRefundForm({ ...refundForm, notes: e.target.value })}
                placeholder="Additional details..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRefundDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRefundOrder}
              disabled={processing || !refundForm.reason || !refundForm.amount || !summary?.can_refund}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {processing ? 'Processing...' : 'Process Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
